import { useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect } from "react"

export interface AnalyticsData {
  gaClientId?: string
  gaSessionId?: number
  gaSessionNumber?: number
  source: string | null
  medium: string | null
  campaign: string | null
  term: string | null
  content: string | null
  metaEventId?: string
  fbp?: string
  fbc?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  consent?: {
    analytics_storage?: "granted" | "denied"
    ad_storage?: "granted" | "denied"
  }
}

export const analyticsAtom = atomWithStorage<AnalyticsData | null>(
  "analytics",
  null
)

let hookInstanceCount = 0 // Globální počítadlo instancí

export function useTrafficSource() {
  const [analytics, setAnalytics] = useAtom(analyticsAtom)

  useEffect(() => {
    if (typeof window === "undefined") return

    hookInstanceCount++
    const isPrimaryInstance = hookInstanceCount === 1 // Logiku spustí jen první instance

    if (!isPrimaryInstance) {
      hookInstanceCount--
      return
    }

    const url = new URL(window.location.href)
    const params = url.searchParams

    let newSource = params.get("utm_source")
    let newMedium = params.get("utm_medium")
    const newCampaign = params.get("utm_campaign")
    const newTerm = params.get("utm_term")
    const newContent = params.get("utm_content")

    const newGclid = params.get("gclid")
    const newGbraid = params.get("gbraid")
    const newWbraid = params.get("wbraid")
    const newSrsltid = params.get("srsltid")
    const newGadSource = params.get("gad_source")
    const newFbclid = params.get("fbclid")
    const newEcmid = params.get("ecmid")
    const newRef = params.get("ref")
    const newFrom = params.get("from")
    const hasSklikParam = params.has("sklik")

    // --- Auto-detect source/medium if missing ---

    // 1. Ecomail
    if (newEcmid && !newSource) {
      newSource = "ecomail"
      if (!newMedium) newMedium = "email"
    }

    // 2. Google Ads (gclid/gbraid/wbraid/gad_source present)
    if ((newGclid || newGbraid || newWbraid || newGadSource) && !newSource) {
      newSource = "google"
      if (!newMedium) newMedium = "cpc"
    }

    // 3. Google Shopping Free Listings (srsltid)
    if (newSrsltid && !newSource) {
      newSource = "google"
      if (!newMedium) newMedium = "organic_shopping"
    }

    // 4. Meta / Facebook (fbclid)
    if (newFbclid && !newSource) {
      newSource = "facebook"
      if (!newMedium) newMedium = "social"
    }

    // 5. Sklik
    if (
      (newRef === "sklik" || newFrom === "sklik" || hasSklikParam) &&
      !newSource
    ) {
      newSource = "seznam"
      if (!newMedium) newMedium = "cpc"
    }

    // _ga cookie format: GA1.1.968393666.1706606015
    // Client ID: 968393666.1706606015
    const getCookie = (name: string) => {
      try {
        const v = document.cookie.match("(^|;) ?" + name + "=([^;]*)(;|$)")
        return v ? v[2] : null
      } catch (e) {
        return null
      }
    }

    const parseGa4SessionFromCookie = (cookieValue: string | null) => {
      if (!cookieValue)
        return { gaSessionId: undefined, gaSessionNumber: undefined }

      // New GA4 cookie format (GS2...): contains s<sessionId> and o<sessionNumber>
      const sidMatch = cookieValue.match(/s(\d{9,})/)
      const snMatch = cookieValue.match(/o(\d{1,})/)

      const sid = sidMatch ? Number(sidMatch[1]) : undefined
      const sn = snMatch ? Number(snMatch[1]) : undefined

      return {
        gaSessionId: Number.isFinite(sid) ? sid : undefined,
        gaSessionNumber: Number.isFinite(sn) ? sn : undefined,
      }
    }

    const checkCookiesAndUpdate = () => {
      const gaCookie = getCookie("_ga")
      let gaClientId: string | undefined = undefined
      if (gaCookie) {
        const parts = gaCookie.split(".")
        if (parts.length >= 4) gaClientId = parts.slice(2).join(".")
      }

      const ga4MeasurementId = process.env.GATSBY_GA4_MEASUREMENT_ID || ""
      const gaCookieName = ga4MeasurementId
        ? "_ga_" + ga4MeasurementId.replace("G-", "").replace(/-/g, "")
        : null

      const ga4Cookie = gaCookieName ? getCookie(gaCookieName) : null
      const { gaSessionId, gaSessionNumber } =
        parseGa4SessionFromCookie(ga4Cookie)

      const fbpCookie = getCookie("_fbp")
      const fbcCookie = getCookie("_fbc")
      // If _fbc cookie is missing but we have an fbclid URL param, construct it
      // per Meta spec: fb.<subdomainIndex>.<creationTime>.<fbclid>
      const fbcDerived =
        fbcCookie ||
        (newFbclid
          ? `fb.1.${Date.now()}.${newFbclid}`
          : undefined)

      setAnalytics(prev => {
        const val = (v: string | null | undefined) => v || null

        const hasNewCampaign =
          newSource ||
          newMedium ||
          newCampaign ||
          newGclid ||
          newGbraid ||
          newWbraid ||
          newSrsltid ||
          newFbclid ||
          newEcmid ||
          newRef ||
          newFrom ||
          hasSklikParam

        const newGaIdFound = gaClientId && prev?.gaClientId !== gaClientId
        const newGaSessionFound =
          (gaSessionId && prev?.gaSessionId !== gaSessionId) ||
          (gaSessionNumber && prev?.gaSessionNumber !== gaSessionNumber)
        const newFbpFound = fbpCookie && prev?.metaEventId !== fbpCookie
        const newFbcFound = fbcDerived && prev?.fbc !== fbcDerived

        if (!prev) {
          return {
            gaClientId,
            gaSessionId,
            gaSessionNumber,
            source: val(newSource),
            medium: val(newMedium),
            campaign: val(newCampaign),
            term: val(newTerm),
            content: val(newContent),
            gclid: newGclid || undefined,
            gbraid: newGbraid || undefined,
            wbraid: newWbraid || undefined,
            metaEventId: fbpCookie || undefined,
            fbp: fbpCookie || undefined,
            fbc: fbcDerived || undefined,
            consent: undefined,
          }
        }

        if (
          hasNewCampaign ||
          newGaIdFound ||
          newGaSessionFound ||
          newFbpFound ||
          newFbcFound
        ) {
          return {
            ...prev,
            gaClientId: gaClientId || prev.gaClientId,
            gaSessionId: gaSessionId || prev.gaSessionId,
            gaSessionNumber: gaSessionNumber || prev.gaSessionNumber,
            source: newSource || prev.source,
            medium: newMedium || prev.medium,
            campaign: newCampaign || prev.campaign,
            term: newTerm || prev.term,
            content: newContent || prev.content,
            gclid: newGclid || prev.gclid,
            gbraid: newGbraid || prev.gbraid,
            wbraid: newWbraid || prev.wbraid,
            metaEventId: fbpCookie || prev.metaEventId,
            fbp: fbpCookie || prev.fbp,
            fbc: fbcDerived || prev.fbc,
          }
        }

        return prev
      })

      return !!gaClientId
    }

    // Run immediately
    // checkCookiesAndUpdate() // <-- Removed immediate call to avoid initial double-log confusion if interval starts fast, or just keep it.
    // Actually, keep it but maybe log it differently.
    checkCookiesAndUpdate()

    // 1. Interval: Check every 2s indefinitely (cheap operation)
    const interval = setInterval(() => {
      checkCookiesAndUpdate()
    }, 3000)

    // 2. Interaction: Check on any click (User likely clicked "Accept Cookies")
    const clickHandler = () => checkCookiesAndUpdate()
    document.addEventListener("click", clickHandler)

    return () => {
      clearInterval(interval)
      document.removeEventListener("click", clickHandler)
      hookInstanceCount--
    }
  }, [setAnalytics])
}
